import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, ErrorText, Field, Panel, SelectField } from "../../components/ui";
import { fmtPrice, fmtRate } from "../../lib/format";
import { PORTFOLIO_ALLOWED_TYPES } from "../portfolio/api";
import { usePortfolio } from "../portfolio/hooks";
import { useCreateTransaction, useDebounced, useSecuritySearch } from "./hooks";
import type { Security, TxnType } from "./api";

const TYPES: { value: TxnType; label: string }[] = [
  { value: "buy", label: "Achat" },
  { value: "sell", label: "Vente" },
  { value: "dividend", label: "Dividende" },
  { value: "deposit", label: "Dépôt" },
  { value: "withdrawal", label: "Retrait" },
  { value: "fee", label: "Frais" },
  { value: "split", label: "Split" },
];
const NEEDS_SECURITY: TxnType[] = ["buy", "sell", "dividend", "split"];

export function NewTransactionPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const create = useCreateTransaction(id as string);
  const portfolio = usePortfolio(id);
  const kind = portfolio.data?.kind;

  const [type, setType] = useState<TxnType>("buy");
  const [security, setSecurity] = useState<Security | null>(null);
  const [securityQuery, setSecurityQuery] = useState("");
  const [tradeDate, setTradeDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [quantity, setQuantity] = useState("");
  const [unitPrice, setUnitPrice] = useState("");
  const [commissionSgi, setCommissionSgi] = useState("");

  const debouncedQuery = useDebounced(securityQuery);
  // only offer securities the portfolio kind may hold (SGI: actions/obligations, SGO: FCP)
  const search = useSecuritySearch(
    security ? "" : debouncedQuery,
    kind ? PORTFOLIO_ALLOWED_TYPES[kind] : undefined,
  );

  function pickSecurity(s: Security) {
    setSecurity(s);
    setSecurityQuery(`${s.ticker} — ${s.name}`);
    // bonds are bought at their acquisition price, not a market quote
    const price = s.type === "obligation" ? s.face_value : s.latest_close;
    if (price) setUnitPrice(price);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    await create.mutateAsync({
      type,
      securityId: NEEDS_SECURITY.includes(type) ? security?.id : undefined,
      tradeDate,
      quantity,
      unitPrice,
      commissionSgi: commissionSgi || undefined,
    });
    navigate(`/portfolios/${id}/transactions`, { replace: true });
  }

  const needsSecurity = NEEDS_SECURITY.includes(type);

  return (
    <Panel title="Nouvelle transaction">
      <form className="inline-form form-stack" onSubmit={submit}>
        <SelectField
          label="Type"
          value={type}
          onChange={(e) => {
            setType(e.target.value as TxnType);
            setSecurity(null);
            setSecurityQuery("");
          }}
        >
          {TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </SelectField>

        {needsSecurity && (
          <div className="field">
            <span>Titre</span>
            <input
              value={securityQuery}
              onChange={(e) => {
                setSecurity(null);
                setSecurityQuery(e.target.value);
              }}
              placeholder={
                kind === "sgo" ? "Rechercher un FCP…" : "Rechercher une action ou une obligation…"
              }
              required
            />
            {!security && search.data && search.data.length > 0 && (
              <ul className="security-suggestions">
                {search.data.map((s) => (
                  <li key={s.id} onClick={() => pickSecurity(s)}>
                    <strong>{s.ticker}</strong> — {s.name}
                    {s.type === "obligation" && s.nominal_rate ? (
                      <span className="muted">
                        {" "}
                        · {fmtRate(s.nominal_rate)} · {s.duration_months} mois
                      </span>
                    ) : (
                      s.latest_close && <span className="muted"> · {fmtPrice(s.latest_close)}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {security?.type === "obligation" && security.nominal_rate && (
              <span className="muted">
                Obligation {fmtRate(security.nominal_rate)} sur {security.duration_months} mois —
                valorisée au prix d'acquisition + coupons courus (pas de cours).
              </span>
            )}
          </div>
        )}

        <Field
          label="Date"
          type="date"
          value={tradeDate}
          onChange={(e) => setTradeDate(e.target.value)}
          required
        />
        <Field
          label="Quantité"
          inputMode="decimal"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          required
        />
        <Field
          label="Prix unitaire"
          inputMode="decimal"
          value={unitPrice}
          onChange={(e) => setUnitPrice(e.target.value)}
          required
        />
        <Field
          label="Commission SGI (FCFA)"
          inputMode="decimal"
          value={commissionSgi}
          onChange={(e) => setCommissionSgi(e.target.value)}
        />

        <ErrorText error={create.error} />
        <Button type="submit" disabled={create.isPending}>
          {create.isPending ? "…" : "Enregistrer"}
        </Button>
      </form>
    </Panel>
  );
}
